import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import type { MdsWorksheetVersion } from 'src/services/documentService/mdsWorksheet';
import { actorLabel, shortDate } from './worksheet-utils';

/**
 * BRK-1856: submission version history + load-previous. Loading a version
 * pulls its immutable items into the editor as reviewable drafts (client-side
 * fill — nothing is written until the user submits again).
 */

type Props = {
  open: boolean;
  onClose: () => void;
  versions: MdsWorksheetVersion[];
  onLoad: (version: MdsWorksheetVersion) => Promise<void>;
};

const VersionHistoryDialog: React.FC<Props> = ({ open, onClose, versions, onLoad }) => {
  const [loadingUuid, setLoadingUuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async (version: MdsWorksheetVersion) => {
    setLoadingUuid(version.uuid);
    setError(null);
    try {
      await onLoad(version);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to load version');
    } finally {
      setLoadingUuid(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Submission history</DialogTitle>
      <DialogContent>
        {versions.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 2 }}
          >
            No submissions yet.
          </Typography>
        ) : (
          <Stack
            divider={<Divider flexItem />}
            gap={1}
          >
            {versions.map((version) => (
              <Stack
                key={version.uuid}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ py: 1 }}
              >
                <Stack sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2">
                    v{version.versionNumber}
                    {version.submittedByName || version.submittedByRole
                      ? ` · ${actorLabel(version.submittedByName, version.submittedByRole)}`
                      : ''}
                    {version.submittedAt ? ` · ${shortDate(version.submittedAt)}` : ''}
                  </Typography>
                  {version.note && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {version.note}
                    </Typography>
                  )}
                </Stack>
                <Button
                  size="small"
                  onClick={() => handleLoad(version)}
                  disabled={loadingUuid !== null}
                  startIcon={
                    loadingUuid === version.uuid ? <CircularProgress size={14} /> : undefined
                  }
                >
                  Load into editor
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
        {error && (
          <Typography
            color="error"
            sx={{ mt: 1 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryDialog;
